"use client";

import React from "react";
import { cn } from "utils";
import type { EmailLoginVariant } from "../types";

const CODE_LENGTH = 6;

const CELL_CLASS: Record<EmailLoginVariant, string> = {
  desktop:
    "w-11 h-11 rounded-lg text-center body_medium_l text-content_primary caret-content_brand_primary bg-background_white_overlay5 border-border_white_transparent5 border focus:outline-none",
  popup:
    "h-11 w-11 rounded-lg text-center body_medium_l text-content_primary caret-content_brand_primary bg-background_white_overlay5 border-border_white_transparent5 border focus:outline-none",
  mobile:
    "min-w-0 flex-1 h-[53.5px] max-w-[53.5px] rounded-lg border text-center body_medium_l text-content_primary caret-content_brand_primary border-border_white_transparent5 bg-background_white_overlay5 focus:outline-none",
};

const MOBILE_V301_CELL_CLASS =
  "min-w-0 flex-1 h-[53.5px] max-w-[53.5px] rounded-lg border border-border_solid bg-background_solid_neutral_primary text-center body_medium_l text-content_primary caret-content_brand_primary focus:outline-none";

interface CodeInputGroupProps {
  variant: EmailLoginVariant;
  code: string[];
  onCodeChange: (index: number, value: string) => void;
  codeError: boolean;
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  /** V3 flat VerifyEmail — solid neutral cells (Figma 96154:99531). */
  useV301Chrome?: boolean;
}

export function CodeInputGroup({
  variant,
  code,
  onCodeChange,
  codeError,
  inputRefs,
  useV301Chrome = false,
}: CodeInputGroupProps) {
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      onCodeChange(index - 1, "");
    }
  };

  const handleInput = (index: number, value: string) => {
    const char = value.replace(/\D/g, "").slice(-1);
    onCodeChange(index, char);
    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    pasted.split("").forEach((char, i) => onCodeChange(i, char));
    const nextFocus = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[nextFocus]?.focus();
  };

  return (
    <div
      className={cn(
        "flex w-full gap-2",
        variant === "desktop" && "justify-between",
      )}
    >
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={code[i] ?? ""}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          aria-label={`Digit ${i + 1}`}
          className={cn(
            useV301Chrome && variant === "mobile"
              ? MOBILE_V301_CELL_CLASS
              : CELL_CLASS[variant],
            codeError &&
              !(useV301Chrome && variant === "mobile") &&
              "border-content_accent2_primary",
          )}
        />
      ))}
    </div>
  );
}

export function focusFirstCodeInput(
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>,
  mode: "immediate" | "raf" = "immediate",
) {
  if (mode === "raf") {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
    });
    return;
  }

  setTimeout(() => {
    inputRefs.current[0]?.focus();
  }, 0);
}
